import {
	DEFAULT_PM_RULE_SOURCE,
	EventType,
	type FieldValue,
	type IFieldChange,
	PmAssignedBy,
	WorkOrderChangeOrigin,
	WorkOrderSource,
	WorkOrderSyncAction,
} from '@atlas/types'
import { formatShortDateTime } from '@/lib/format-short-date-time'
import { formatMoney } from '@/lib/money'
import type { EventDTO } from '@/services/activity.service'
import { statusLabel } from './work-order.types'

/** What one History line records. */
export enum HistoryEntryKind {
	CREATED = 'created',
	FIELDS = 'fields',
	STATUS = 'status',
	SYNC = 'sync',
}

/** One audited change of a work order, as the History tab lists it. */
export interface HistoryEntry {
	id: string
	at: string
	kind: HistoryEntryKind
	/** The ATLAS user behind the change, when there is one. */
	actorId?: string
	origin: WorkOrderChangeOrigin
	/** The line's headline, without its actor ("changed 2 fields"). */
	summary: string
	/** Each field with its value before and after, formatted for reading. */
	changes: { field: string; before: string; after: string }[]
	/** Messages the sync raised, such as a kept ATLAS cost. */
	warnings: string[]
}

/** What the sync did to the order, as a History line says it. */
export const SYNC_ACTION_LABEL: Record<WorkOrderSyncAction, string> = {
	[WorkOrderSyncAction.CREATE]: 'created it',
	[WorkOrderSyncAction.UPDATE]: 'updated it',
	[WorkOrderSyncAction.FLAG_CLOSED]: 'flagged it closed',
	[WorkOrderSyncAction.FLAG_REMOVED]: 'flagged it removed',
	[WorkOrderSyncAction.HOLD]: 'held it for a mapping',
	[WorkOrderSyncAction.NONE]: 'found nothing to change',
}

/** Who made a change, as the History tab names a change without an ATLAS user. */
export const ORIGIN_LABEL: Record<WorkOrderChangeOrigin, string> = {
	[WorkOrderChangeOrigin.USER]: 'A user',
	[WorkOrderChangeOrigin.WORKFRONT]: 'Workfront sync',
	[WorkOrderChangeOrigin.SYSTEM]: 'System',
}

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** Fields whose generated name would read wrong. */
const FIELD_LABELS: Record<string, string> = { pmUserId: 'PM' }

/** Fields that hold a user ID, shown by the user's name. */
const USER_FIELDS = new Set(['pmUserId'])

/** How the history names what an event holds as a code or an ID; each falls back to the raw value. */
export interface HistoryNames {
	geography?: (code: string) => string
	user?: (userId: string) => string | undefined
}

/** A field's API name in the reader's words: `dueAt` → "Due at", `tuple.brand` → "Tuple brand", `pmUserId` → "PM". */
export function fieldLabel(field: string): string {
	const fixed = FIELD_LABELS[field]
	if (fixed) return fixed
	const words = field
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[._]+/g, ' ')
		.trim()
		.toLowerCase()
	return words ? words.charAt(0).toUpperCase() + words.slice(1) : field
}

const isMoney = (value: object): value is { amountMinor: number; currency: string } =>
	'amountMinor' in value && 'currency' in value

/** A compared value as text: "—" for none, lists comma-joined, money and timestamps formatted. */
export function formatFieldValue(value: FieldValue | undefined): string {
	if (value === null || value === undefined) return '—'
	if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
	if (typeof value === 'object') return isMoney(value) ? formatMoney(value) : JSON.stringify(value)
	if (typeof value === 'boolean') return value ? 'Yes' : 'No'
	if (typeof value === 'string' && ISO_DATE_TIME.test(value)) return formatShortDateTime(value)
	return value === '' ? '—' : String(value)
}

const formatChanges = (changes: unknown, names: HistoryNames): HistoryEntry['changes'] =>
	(Array.isArray(changes) ? (changes as IFieldChange[]) : []).map((change) => {
		const format = (value: FieldValue | undefined) =>
			USER_FIELDS.has(change.field) && typeof value === 'string'
				? (names.user?.(value) ?? value)
				: formatFieldValue(value)
		return { field: fieldLabel(change.field), before: format(change.before), after: format(change.after) }
	})

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

const isOrigin = (value: unknown): value is WorkOrderChangeOrigin =>
	Object.values(WorkOrderChangeOrigin).includes(value as WorkOrderChangeOrigin)

type Payload = Record<string, unknown>
type Built = Omit<HistoryEntry, 'id' | 'at' | 'actorId'>

const userOrSystem = (e: EventDTO) => (e.userId ? WorkOrderChangeOrigin.USER : WorkOrderChangeOrigin.SYSTEM)

function updated(d: Payload, e: EventDTO, names: HistoryNames): Built {
	const changes = formatChanges(d.fieldChanges, names)
	// An event written before field detail existed still names the fields it touched.
	const named = Array.isArray(d.changes) ? (d.changes as string[]).map(fieldLabel) : []
	return {
		kind: HistoryEntryKind.FIELDS,
		origin: isOrigin(d.origin) ? d.origin : userOrSystem(e),
		summary: changes.length
			? `changed ${plural(changes.length, 'field')}`
			: named.length
				? `changed ${named.join(', ')}`
				: 'edited the work order',
		changes,
		warnings: [],
	}
}

function syncApplied(d: Payload): Built {
	const action = SYNC_ACTION_LABEL[d.action as WorkOrderSyncAction] ?? 'applied a version'
	const version = typeof d.version === 'number' ? ` version ${d.version}` : ''
	const warnings = Array.isArray(d.warnings)
		? (d.warnings as { message?: unknown }[]).flatMap((w) => (typeof w.message === 'string' ? [w.message] : []))
		: []
	// The fields the sync wrote are listed by the `work_order.updated` it emitted beside this.
	return {
		kind: HistoryEntryKind.SYNC,
		origin: WorkOrderChangeOrigin.WORKFRONT,
		summary: `applied Workfront${version}: ${action}${d.backfill === true ? ' (backfill)' : ''}`,
		changes: [],
		warnings,
	}
}

/**
 * A PM the Workfront sync gave the order, from a rule or its parent. A PM a person chose is
 * already a field change of its `work_order.updated`, so it is not listed twice.
 */
function pmAssigned(d: Payload, _e: EventDTO, names: HistoryNames): Built | null {
	if (d.pmAssignedBy === PmAssignedBy.RULE) {
		const geography = typeof d.ruleGeography === 'string' ? d.ruleGeography : null
		return {
			kind: HistoryEntryKind.SYNC,
			origin: WorkOrderChangeOrigin.WORKFRONT,
			summary:
				geography && geography !== DEFAULT_PM_RULE_SOURCE
					? `set the PM from the PM rule for ${names.geography?.(geography) ?? geography}`
					: 'set the PM from the default PM rule',
			changes: [],
			warnings: [],
		}
	}
	if (d.pmAssignedBy === PmAssignedBy.PARENT) {
		return {
			kind: HistoryEntryKind.SYNC,
			origin: WorkOrderChangeOrigin.WORKFRONT,
			summary: "set the PM to its parent work order's PM",
			changes: [],
			warnings: [],
		}
	}
	return null
}

const BUILDERS: Partial<Record<string, (d: Payload, e: EventDTO, names: HistoryNames) => Built | null>> = {
	[EventType.WORK_ORDER_CREATED]: (d, e, names) => ({
		kind: HistoryEntryKind.CREATED,
		origin: d.source === WorkOrderSource.WORKFRONT ? WorkOrderChangeOrigin.WORKFRONT : userOrSystem(e),
		summary: 'created this work order',
		// The PM chosen on the create form; one the sync gives is a line of its own.
		changes: formatChanges(
			typeof d.pmUserId === 'string' ? [{ field: 'pmUserId', before: null, after: d.pmUserId }] : [],
			names,
		),
		warnings: [],
	}),
	[EventType.WORK_ORDER_UPDATED]: updated,
	[EventType.WORK_ORDER_STATUS_CHANGED]: (d, e) => {
		const status = (side: unknown) => {
			const code = (side as { status?: unknown } | undefined)?.status
			return typeof code === 'string' ? statusLabel(code) : '—'
		}
		return {
			kind: HistoryEntryKind.STATUS,
			origin: userOrSystem(e),
			summary: 'moved the status',
			changes: [{ field: 'Status', before: status(d.from), after: status(d.to) }],
			warnings: [],
		}
	},
	[EventType.WORK_ORDER_SYNC_APPLIED]: syncApplied,
	[EventType.WORK_ORDER_PM_ASSIGNED]: pmAssigned,
}

/**
 * The audited changes of a page of the work order's audit log, in the order the server
 * returned them (newest first): creation, field changes, status changes, what the
 * Workfront sync did and the PMs it filled. Every other event type is Activity, not History.
 */
export function buildHistoryEntries(events: EventDTO[], names: HistoryNames = {}): HistoryEntry[] {
	return events.flatMap((e) => {
		const built = BUILDERS[e.type]?.(e.data ?? {}, e, names)
		if (!built) return []
		return [{ id: e.eventId, at: e.timestamp, actorId: e.userId, ...built }]
	})
}
